import { RWSService } from "@rws-framework/client";
import { deepEqual } from "src/helpers/utils";
import { HTTPClient } from "src/services/httpClient";
import StorageService, { StorageServiceInstance } from "src/services/StorageService";
import type { Component } from "src/types/component";

export type InstructorNote = {
  id: string | number;
  title: string;
  icon?: string;
  type?: string;
}

export type InstructorNotesContext = {
  courseId?: string;
  lessonId?: string;
  pageId?: string;
}

export type InstructorNotesSetResponse = {
  instructor_notes: InstructorNote[];
}

export type InstructorNotesAll = Record<string, InstructorNote[]> & {
  course_notes?: Component[];
}

class InstructorNotesService extends RWSService {
  static _IN_CLIENT: boolean = true;
  private client = new HTTPClient({});

  constructor(
    @StorageService private storageService: StorageServiceInstance
  ) {
    super();
  }

  get(context: InstructorNotesContext): Promise<InstructorNote[]> {
    const all: InstructorNote[] = [{
      id: 0,
      title: "All Instructor Resources",
      icon: "fa-pencil-square-o"
    }];

    return new Promise((resolve, reject) => {
      this.storageService.get('course', context, (course) => {
        const notes = course.instructor_notes;
        const has_no_notes = !Array.isArray(notes) && deepEqual(notes, {});
        if (!has_no_notes) {
          const location = context.pageId || context.lessonId || "instructor_notes";
          if (location == "instructor_notes") {
            resolve(all.concat(course.instructor_notes[location] || []));
          } else {
            if (course.instructor_notes[location] === undefined) {
              /*
                both _page.html and page_edit.html include the panel-bottom directive so in this case the server is called twice
                hence setting the resources to [] prevents the 2nd time from calling GET:
                  call 1: resources == undefined
                  call 1: resources = []
                  call 1: GET <wait>
                  call 2: resources = [] returned
                  call 1: resources = result returned
                note that if call 2 comes in after call 1 resolves, resources != undefined so this has no race condition
                TODO obviously the real fix is to route things appropriately so this is not an issue
              */
              course.instructor_notes[location] = [];
              // instructor:notes:lesson / instructor:notes:page (selected by optional pageId)
              this.client.get(`/instructor_notes/${context.courseId}/${context.lessonId}${context.pageId ? '/' + context.pageId : ''}`)
              .then((result) => {
                course.instructor_notes[location] = result;
                resolve(all.concat(result));
              }).catch(() => {
                reject(new Error("Failed to get instructor notes."));
              });
            } else {
              resolve(all.concat(course.instructor_notes[location]));
            }
          }
        } else {
          if (!context.lessonId) {
            resolve(all);
          } else {
            const current_view = context.pageId ? 'page' : 'lesson';
            this.storageService.get(current_view, context, (obj) => {
              if (obj.instructor_notes && obj.instructor_notes.length) {
                resolve(all.concat(obj.instructor_notes));
              } else {
                resolve(all);
              }
            }, () => {
              reject(new Error("There are no instructor notes."));
            });
          }
        }
      });
    });
  }

  /*  this will override existing instructor notes for the context  */
  set(
    context: InstructorNotesContext,
    notes_for_context: InstructorNote[]
  ): Promise<InstructorNotesSetResponse> {
    const url_fragment = `${context.courseId}${context.lessonId ? `/${context.lessonId}${context.pageId ? `/${context.pageId}` : ''}` : ''}`;

    // instructor:notes:course, instructor:notes:lesson, or instructor:notes:page
    return this.client.post(`/instructor_notes/${url_fragment}`, {
      body: { instructor_notes: notes_for_context }
    });
  }

  // instructor:notes:all
  public get_all(courseId: string): Promise<InstructorNotesAll> {
    return this.client.get(`/instructor_notes_all/${courseId}`);
  }
}

export default InstructorNotesService.getSingleton();
export { InstructorNotesService as InstructorNotesServiceInstance };
